<template>
  <b-container fluid>
    <b-breadcrumb :items="breadcrumbs"/>
    <b-row>
      <b-col class="mb-3" lg="6">
        <b-card title="Build a Report">
          <p class="card-text">
            Choose from the options below to filter your results here.
          </p>
          <report-filters :workflows="workflows" v-model="filters" v-on:update:filters="updateFilters"></report-filters>
        </b-card>
      </b-col>
      <b-col class="mb-3" lg="6">
        <b-card title="Report Type">
          <b-form-group label="Data Grouping:">
            <b-form-radio-group v-model='groupBy' :options="groupByOptions" name="group-by" stacked></b-form-radio-group>
            <b-alert variant="warning" :show="showGroupByWarning">
              The selected option will show multiple lines with duplicate data for any event that has more than one issue.
            </b-alert>
          </b-form-group>
          <b-form-group label="Data Format:">
            <b-form-radio-group v-model="reportType" :options="reportTypeOptions" name="report-type"></b-form-radio-group>
          </b-form-group>
          <b-form-group label="Download File Format:" v-if="reportType == 'download'">
            <b-form-radio-group v-model="downloadType" :options="downloadTypeOptions" name="download-type"></b-form-radio-group>
          </b-form-group>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card :header="reportHeader" v-if="tableFields.length > 0">
          <b-table :fields="tableFields" :items="tableItems" striped bordered></b-table>

          <b-pagination v-if="itemCount" align="center" size="md" :total-rows="itemCount" v-model="currentPage" :per-page="pageSize" :limit="pageSize"></b-pagination>
        </b-card>
        <bar-chart v-if="reportType === 'bar'" element-id="report-bar" :group-by="groupBy" :filters="filters" sort="false" header="Report Results" :linkToReport="false" />
        <line-chart v-if="reportType === 'line'" element-id="report-bar" :group-by="groupBy" :filters="filters" sort="false" header="Report Results":linkToReport="false" />
        <donut-chart v-if="reportType === 'doughnut'" element-id="report-bar" :group-by="groupBy" :filters="filters" sort="false" max="10" minHeight="480" header="Report Results" :linkToReport="false" />
        <p class="text-center" v-if="reportType == 'download'">
          <b-button variant="primary" size="lg" v-on:click.stop.prevent="download">Download Now</b-button>
        </p>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import API from '@/lib/api'
import ReportFilters from '@/components/ReportFilters'
import BarChart from '@/components/BarChart'
import LineChart from '@/components/LineChart'
import DonutChart from '@/components/DonutChart'

export default {
  name: 'Report',
  components: { ReportFilters, BarChart, LineChart, DonutChart },
  data () {
    return {
      breadcrumbs: [{text: 'My Vos Voco', to: '/'}, {text: 'Reporting', to: '/reporting'}, {text: 'Build a Report', to: '/reporting/report'}],
      workflows: [],
      reportType: this.$route.query.type || 'table',
      downloadType: this.$route.query.downloadType || 'xlsx',
      groupBy: this.$route.query.group || 'event',
      filters: this.$route.query.filters || {date_range: {type: 'weeks', count: 14}},
      tableFields: [],
      tableItems: [],
      currentPage: parseInt(this.$route.query.page || '1'),
      pageSize: 20,
      itemCount: null,
      chartTypes: {
        event: [],
        issue: [],
        days: ['line'],
        weeks: ['line', 'bar'],
        issue_type: ['bar', 'doughnut'],
        root_cause: ['bar', 'doughnut']
      },
      chartNames: {
        line: 'Line Chart',
        bar: 'Bar Chart',
        doughnut: 'Doughnut Chart'
      }
    }
  },
  created () {
    this.fetchWorkflows()
  },
  computed: {
    reportTypeOptions () {
      return [
        { value: 'table', text: 'Data Table' },
        { value: 'download', text: 'Downloadable File' }
      ].concat(this.chartTypes[this.groupBy].map((chart) => {
        return {value: chart, text: this.chartNames[chart]}
      }))
    },
    downloadTypeOptions () {
      return [
        { value: 'xlsx', text: 'Excel spreadsheet' },
        { value: 'csv', text: 'CSV (plaintext spreadsheet suitable for import into reporting software such as Qlik)' }
      ]
    },
    groupByOptions () {
      return [
        { value: 'event', text: 'One line per event' },
        { value: 'issue', text: 'One line per issue per event' },
        { value: 'days', text: 'Daily summary' },
        { value: 'weeks', text: 'Weekly summary' },
        { value: 'issue_type', text: 'Issue Type summary' },
        { value: 'root_cause', text: 'Root Cause summary' }
      ]
    },
    groupByOptionText () {
      return this.groupByOptions.find((option) => { return option.value === this.groupBy }).text
    },
    showGroupByWarning () {
      return this.groupBy === 'issue'
    },
    workflow () {
      if (this.filters.workflow_id) {
        return this.workflows.find((wf) => { return wf.id === this.filters.workflow_id })
      } else {
        return null
      }
    },
    reportHeader () {
      if (this.itemCount) {
        if (this.itemCount === 1) {
          return '1 Result'
        } else {
          return `${this.itemCount} Results`
        }
      } else {
        return 'Report Results'
      }
    }
  },
  watch: {
    filters () {
      // this.currentPage = 1
      this.updatePath()
    },
    groupBy () {
      this.currentPage = 1
      if (!this.chartTypes[this.groupBy].includes(this.reportType)) {
        this.reportType = 'table'
      }
      this.updatePath()
    },
    reportType () {
      this.currentPage = 1
      this.updatePath()
    },
    '$route' (to, from) {
      this.currentPage = parseInt(to.query.page || '1')
      this.filters = to.query.filters || this.filters || {date_range: {type: 'weeks', count: 14}}
      this.groupBy = to.query.group || 'event'
      this.reportType = to.query.type || 'table'
      this.updateTable()
    },
    currentPage () {
      this.updatePath()
    }
  },
  methods: {
    fetchWorkflows () {
      API.get('workflows', {context: 'events', deep: true}, this.$store.getters.authHeaders)
        .then((response) => {
          this.workflows = response.data.workflows
        })
    },
    updateFilters (filters) {
      this.filters = filters
      this.updatePath()
    },
    updatePath () {
      this.$router.push({path: '/reporting/report', query: { filters: this.filters, page: this.currentPage, group: this.groupBy, type: this.reportType }})
    },
    updateTable () {
      console.log('Update Table')
      if (this.reportType === 'table' && this.workflow != null) {
        const params = {
          format: 'table',
          group: this.groupBy,
          filter: (this.filters || {}),
          page: this.currentPage,
          page_size: this.pageSize
        }
        API.get('report', params, this.$store.getters.authHeaders)
          .then((response) => {
            this.tableFields = response.data.fields
            this.tableItems = response.data.items
            this.itemCount = response.data.total_count
          })
      } else {
        this.tableFields = []
        this.tableItems = []
      }
    },
    download () {
      const params = {
        format: 'download_url',
        download_format: this.downloadType,
        group: this.groupBy,
        filter: (this.filters || {})
      }
      API.get('report', params, this.$store.getters.authHeaders)
        .then((response) => {
          document.location = response.data.url
        })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/pages/Report.vue